import { customFetch } from '@/lib/custom-fetch';

export interface Company {
    id: number;
    name: string;
    portcoId: string;
    primaryDomain: string;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
}

export interface CompaniesResponse {
    success: boolean;
    data: Company[];
    totalCount: number;
}

export interface ImpersonationRequest {
    companyId: number;
}

export interface ImpersonationResponse {
    success: boolean;
    message: string;
    data: {
        companyId: string;
        portcoId: string;
        companyName: string;
        impersonatedBy: string;
    };
}

export interface ApiError {
    success: false;
    error: string;
    code: string;
}

/**
 * Fetch the list of companies available for impersonation
 * Restricted to @aleph.vc domain users only
 */
export async function fetchCompanies(): Promise<CompaniesResponse> {
    try {
        const response = await customFetch<CompaniesResponse>({
            url: '/api/admin/companies',
            method: 'GET',
        });
        return response;
    } catch (error) {
        console.error('Error fetching companies:', error);
        throw new Error(`Failed to fetch companies: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

/**
 * Start impersonating a company
 * Restricted to @aleph.vc domain users only
 */
export async function startImpersonation(companyId: number): Promise<ImpersonationResponse> {
    try {
        const response = await customFetch<ImpersonationResponse>({
            url: '/api/admin/impersonate',
            method: 'POST',
            data: { companyId },
        });
        return response;
    } catch (error) {
        console.error('Error starting impersonation:', error);
        throw new Error(`Failed to start impersonation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

/**
 * Stop impersonation by returning to Aleph (companyId: 7)
 * This uses the same endpoint as startImpersonation but with companyId 7
 */
export async function stopImpersonation(): Promise<ImpersonationResponse> {
    try {
        const response = await customFetch<ImpersonationResponse>({
            url: '/api/admin/impersonate',
            method: 'POST',
            data: { companyId: 7 }, // Return to Aleph
        });
        return response;
    } catch (error) {
        console.error('Error stopping impersonation:', error);
        throw new Error(`Failed to stop impersonation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}
